import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
    ButtonHTMLAttributes,
    HTMLAttributes,
    MouseEvent,
    ReactNode,
    forwardRef,
    useState,
} from "react";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import styles from "./Chip.module.scss";

/* ════════════════════════════════════════════════════════════════════════
   Treez DS — Chip
   Source: Figma `Chip/Outlined` + `Chip/Filled` components
           (file qgFv3OQga6RQ4luvSFuGFP).

   Figma's variant matrix is collapsed into a single, composable API:
   - `Static`     →  default (no icons, no `onClick`, no `onRemove`).
   - `Removable`  →  pass `onRemove`. Renders a Close (X) icon button.
   - `Dropdown`   →  pass `iconRight={<ChevronDownIcon />}`.
   - `Filter`     →  pass `iconRight={<TuneIcon />}`.
   - `Action`     →  pass `iconLeft={<PlusIcon />}` (or `<CheckIcon />`).
   - `+ Counter`  →  pass `counter` (number badge rendered first).

   Color families:
   - `default` (Outlined): white bg + 1px grey-04 border.
   - `grey | brown | orange | peach | blue | purple | yellow` (Filled):
     no border, bg from the matching secondary palette `-01` shade
     (or `--ds-color-grey-04` for `grey`). On hover/selected it darkens
     to the `-02` shade (or `--ds-color-grey-05` for `grey`).

   States (per Figma):
   - Enabled
   - Hovered  (only when interactive)
   - Focused  (`:focus-visible` → outline 1.5px black)
   - Disabled (filled → bg neutralizes to `--ds-color-grey-02`)
   - Selected (persistent — black outline + filled bg darkens)
   ════════════════════════════════════════════════════════════════════════ */

export type ChipColor =
    | "default"
    | "grey"
    | "brown"
    | "orange"
    | "peach"
    | "blue"
    | "purple"
    | "yellow";

/** Item rendered inside the dropdown menu when `items` is provided. */
export interface ChipDropdownItem {
    /** Stable id; used as React key + returned to `onSelect`. */
    id: string;
    /** Visible label. Can be a string or a node (icon + text, etc.). */
    label: ReactNode;
    /** Callback fired when the item is activated (click or Enter). */
    onSelect?: (id: string) => void;
    /** Renders the item as destructive (red). */
    destructive?: boolean;
    /** Disables the item. */
    disabled?: boolean;
}

export interface ChipProps
    extends Omit<HTMLAttributes<HTMLElement>, "onClick"> {
    /** Visible label. Truncated with ellipsis at 220px max-width. */
    label: ReactNode;
    /**
     * Visual family. `default` is the outlined chip (white bg + 1px grey
     * border). Any other value renders a filled chip using the matching
     * secondary palette. Defaults to `default`.
     */
    color?: ChipColor;
    /** Optional icon rendered before the label (e.g. `+`, `✓`). */
    iconLeft?: ReactNode;
    /**
     * Optional icon rendered after the label (e.g. chevron, filter tune).
     * Ignored when `onRemove` is set — the Close (X) icon takes its place.
     */
    iconRight?: ReactNode;
    /** When provided, renders a black badge with the value before the label. */
    counter?: number | string;
    /**
     * Persistent selected state. Visually identical to keyboard focus
     * (1.5px black outline). Use for filter chips that stay "on".
     */
    selected?: boolean;
    /** Disables interactions and applies the disabled visual style. */
    disabled?: boolean;
    /**
     * Click handler for the chip body. When set, the chip renders as a
     * `<button>` and gains hover state. If both `onClick` and `onRemove`
     * are set, the X icon's click is stopPropagation'd so only `onRemove`
     * fires when the user clicks it.
     */
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    /**
     * Removal handler. When set, an X (Close) icon is rendered after the
     * label as its own focusable button. Overrides `iconRight`.
     */
    onRemove?: (event: MouseEvent<HTMLButtonElement>) => void;
    /** Custom icon for the remove button (defaults to a built-in X). */
    removeIcon?: ReactNode;
    /** Custom aria-label for the remove button. Defaults to `"Remove"`. */
    removeAriaLabel?: string;
    /** Forwarded type when the chip itself renders as a `<button>`. */
    type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
    /**
     * When provided, the chip becomes a dropdown trigger:
     * - A chevron-down icon is auto-rendered on the right (unless you
     *   already passed `iconRight`).
     * - Clicking or pressing Enter/Space opens a Radix DropdownMenu.
     * - Chevron rotates 180° while the menu is open.
     * - The chip gets the same 1.5px black outline as `selected` while open.
     *
     * Ignored if `onRemove` is set (a removable chip can't open a menu —
     * use a separate menu trigger or compose your own).
     */
    items?: ChipDropdownItem[];
    /** Where the dropdown menu opens relative to the chip. */
    menuAlign?: "start" | "center" | "end";
    /** Vertical placement of the dropdown menu. */
    menuSide?: "top" | "right" | "bottom" | "left";
    /** Distance in px between the chip and the menu. */
    menuSideOffset?: number;
}

/* ─────────────── Built-in icons (so default variants work out of the box) */

/** Default Close (X) icon used when `onRemove` is set with no `removeIcon`. */
const CloseIcon = () => <Icon icon="close" />;

/** Chevron-down auto-injected when `items` is provided. Rotates via CSS on `.icon`. */
const ChevronDownIcon = () => <Icon icon="chevron-down" />;

/**
 * Treez DS — Chip (Wave 1).
 *
 * Source: Figma `Chip/Outlined` component (file `qgFv3OQga6RQ4luvSFuGFP`).
 *
 * Design intent:
 * - 32px tall pill with 12px radius and a 1px outline border.
 * - Text label truncated with ellipsis at a 220px max-width.
 * - Optional leading/trailing icon slots (18px) and an optional black
 *   counter badge that sits before the label.
 *
 * The component renders as:
 * - `<span>`   when neither `onClick` nor `onRemove` is set (purely static).
 * - `<button>` when only `onClick` is set (the entire chip is the trigger).
 * - `<div>`    when `onRemove` is set — the X is its own nested
 *              `<button>`. If `onClick` is also set, the body becomes a
 *              click target and X stops propagation.
 */
export const Chip = forwardRef<HTMLElement, ChipProps>(function Chip(
    {
        label,
        color = "default",
        iconLeft,
        iconRight,
        counter,
        selected = false,
        disabled = false,
        onClick,
        onRemove,
        removeIcon,
        removeAriaLabel = "Remove",
        className,
        type = "button",
        items,
        menuAlign = "start",
        menuSide = "bottom",
        menuSideOffset = 6,
        ...rest
    },
    ref,
) {
    const hasRemove = Boolean(onRemove);
    /* `items` turns the chip into a dropdown trigger and is incompatible
       with `onRemove`. We silently drop `items` in that case. */
    const hasMenu = Boolean(items && items.length > 0) && !hasRemove;
    const isClickable = (Boolean(onClick) || hasMenu) && !disabled;

    /* When the body is clickable AND there is a removable X, we render
       the body as a div with role=button so we can keep the X as its own
       focusable <button> (avoids nesting <button> inside <button>).
       Dropdown chips always render as <button> so Radix can manage focus. */
    const renderAsButton = (isClickable && !hasRemove) || hasMenu;
    const renderAsClickableDiv = isClickable && hasRemove;
    const RootTag = renderAsButton
        ? "button"
        : hasRemove || isClickable
        ? "div"
        : "span";

    /* Auto-inject a chevron when `items` is provided and the consumer
       didn't already supply their own trailing icon. */
    const isAutoChevron = hasMenu && !iconRight && !hasRemove;
    const resolvedIconRight = isAutoChevron ? <ChevronDownIcon /> : iconRight;

    const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (disabled) return;
        onRemove?.(event);
    };

    const handleBodyClick = (event: MouseEvent<HTMLElement>) => {
        if (disabled) return;
        onClick?.(event);
    };

    const trailing = hasRemove ? (
        <button
            aria-label={removeAriaLabel}
            className={styles.removeButton}
            disabled={disabled}
            onClick={handleRemove}
            tabIndex={disabled ? -1 : 0}
            type="button"
        >
            <span aria-hidden className={styles.icon}>
                {removeIcon ?? <CloseIcon />}
            </span>
        </button>
    ) : resolvedIconRight ? (
        <span
            aria-hidden
            className={cx(styles.icon, isAutoChevron && styles.chevron)}
        >
            {resolvedIconRight}
        </span>
    ) : null;

    const chipBody = (
        <RootTag
            aria-disabled={disabled || undefined}
            aria-pressed={
                renderAsButton && selected && !hasMenu ? selected : undefined
            }
            className={cx(styles.root, className)}
            data-color={color}
            data-disabled={disabled || undefined}
            data-interactive={
                renderAsButton || renderAsClickableDiv || undefined
            }
            data-selected={selected || undefined}
            data-ui="chip"
            onClick={
                !hasMenu && (renderAsButton || renderAsClickableDiv)
                    ? handleBodyClick
                    : undefined
            }
            ref={ref as never}
            role={renderAsClickableDiv ? "button" : undefined}
            tabIndex={renderAsClickableDiv ? (disabled ? -1 : 0) : undefined}
            type={renderAsButton ? type : undefined}
            {...rest}
        >
            {counter !== undefined && counter !== null && (
                <span className={styles.counter} data-part="counter">
                    {counter}
                </span>
            )}
            {iconLeft && (
                <span aria-hidden className={styles.icon}>
                    {iconLeft}
                </span>
            )}
            <span className={styles.label}>{label}</span>
            {trailing}
        </RootTag>
    );

    /* No items → render the chip directly. */
    if (!hasMenu) return chipBody;

    /* Dropdown variant: wrap the chip with Radix DropdownMenu.
       `asChild` makes Radix use the chip itself as the trigger so the
       chevron rotation tied to `[data-state="open"]` works on `.root`. */
    return (
        <ChipDropdownWrapper
            align={menuAlign}
            disabled={disabled}
            items={items as ChipDropdownItem[]}
            side={menuSide}
            sideOffset={menuSideOffset}
            trigger={chipBody}
        />
    );
});

Chip.displayName = "Chip";

/* ─────────── Internal: Radix DropdownMenu wrapper ─────────── */

interface ChipDropdownWrapperProps {
    trigger: ReactNode;
    items: ChipDropdownItem[];
    disabled?: boolean;
    side: "top" | "right" | "bottom" | "left";
    align: "start" | "center" | "end";
    sideOffset: number;
}

/**
 * Composes `@radix-ui/react-dropdown-menu` around a Chip used as the
 * trigger. Renders the same popover style as `ButtonDropdown` (16px
 * radius, `--ds-shadow-popover`, light-green hover via `primary-light`).
 */
const ChipDropdownWrapper = ({
    trigger,
    items,
    disabled,
    side,
    align,
    sideOffset,
}: ChipDropdownWrapperProps) => {
    const [open, setOpen] = useState(false);
    return (
        <DropdownMenu.Root onOpenChange={setOpen} open={open}>
            <DropdownMenu.Trigger asChild disabled={disabled}>
                {trigger}
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align={align}
                    className={styles.menuContent}
                    loop
                    side={side}
                    sideOffset={sideOffset}
                >
                    {items.map(item => (
                        <DropdownMenu.Item
                            className={cx(
                                styles.menuItem,
                                item.destructive && styles.menuItemDestructive,
                            )}
                            disabled={item.disabled}
                            key={item.id}
                            onSelect={() => item.onSelect?.(item.id)}
                        >
                            {item.label}
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};
